import * as React from "react";
import { AllocationDimensionGrid } from "components/allocation-dimension/allocation-dimension-grid";
import { IAllocationDimension, AllocationDimensionValueFields } from "helpers/allocation-dimension";
import { IPurchaseOrderDoorGroup } from "helpers/delivery-purchase-order";
import { UnitsEntryType } from "constants/product-collection";
import { ISchemaKey } from "helpers/product-collection";
import { ManageAllocationDimensionDescription } from "./manage-allocation-dimension-description";
import { ExcelExportParams, GridApi } from "ag-grid-community";
import moment from "lib/moment-i18n";
import { dateFormat } from "helpers/dates";
import { ExportMode } from "./constants";
import { formatMessage } from "lib/globalize-util";

interface IProps {
    allocationDimension: IAllocationDimension;
    onMutateAllocationDimensionValue: (allocationDimensionId, item) => void;
    onMoveAllocationDimensionValue: (allocationDimensionId: string, index: number, destinationIndex: number) => void;
    readOnly?: boolean;
    merchantPurchaseOrderDoorGroups: IPurchaseOrderDoorGroup[];
    unitsEntryType?: UnitsEntryType;
    onPurchaseOrdersError?: () => void;
    isRollupMode: boolean;
    onCustomDoorCellValueChanged: (
        attributes: ISchemaKey[],
        allocationDimensionId: string,
        rowIndexToDataMap: any
    ) => void;
    collectionsRefs: Map<string, string>;
    setEditDoorAttribute: React.Dispatch<React.SetStateAction<string>>;
    setDeleteMode: React.Dispatch<React.SetStateAction<string>>;
    setDisableEscapeKeyDown: React.Dispatch<React.SetStateAction<boolean>>;
    assortmentName?: string;
}
export const AddOrEditAttributesView: React.FunctionComponent<IProps> = ({
    allocationDimension,
    onMutateAllocationDimensionValue,
    onMoveAllocationDimensionValue,
    readOnly,
    merchantPurchaseOrderDoorGroups,
    unitsEntryType,
    onPurchaseOrdersError,
    isRollupMode,
    onCustomDoorCellValueChanged,
    collectionsRefs,
    setEditDoorAttribute,
    setDeleteMode,
    setDisableEscapeKeyDown,
    assortmentName,
}) => {
    const agGridApiRef = React.useRef<GridApi>(null);

    const handleOnRowUpdated = React.useCallback(
        (_index, rowData) => {
            onMutateAllocationDimensionValue(allocationDimension.id, rowData);
        },
        [allocationDimension.id, onMutateAllocationDimensionValue]
    );
    const handleOnRowMove = React.useCallback(
        (index, destinationIndex) => {
            onMoveAllocationDimensionValue(allocationDimension.id, index, destinationIndex);
        },
        [allocationDimension.id, onMoveAllocationDimensionValue]
    );

    const handleActionInvoked = React.useCallback(
        (field_key: string, deleteMode: boolean) => {
            if (deleteMode) {
                setDeleteMode(field_key);
            } else {
                setEditDoorAttribute(field_key);
            }
        },
        [setDeleteMode, setEditDoorAttribute]
    );

    const handleGridReady = (params) => {
        agGridApiRef.current = params.api;
    };

    const handleExportData = React.useCallback(
        (mode: ExportMode) => {
            if (!agGridApiRef.current) {
                return;
            }
            const formatDate = moment().format(dateFormat.LONG_FULL_DATE_FORMAT);
            const params: ExcelExportParams = {
                fileName: `${assortmentName}_Doors_${formatDate}`,
                processCellCallback: ({ column, value }) => {
                    if (column.getColId() === AllocationDimensionValueFields.Included) {
                        return value ? formatMessage("Yes") : formatMessage("No");
                    }
                    return value;
                },
            };

            if (mode === ExportMode.Excel) {
                params.fileName = `${params.fileName}.xlsx`;
                agGridApiRef.current.exportDataAsExcel(params);
            } else if (mode === ExportMode.Csv) {
                params.fileName = `${params.fileName}.csv`;
                agGridApiRef.current.exportDataAsCsv(params);
            }
        },
        [assortmentName]
    );

    const handleExportDataAsExcel = React.useCallback(() => {
        handleExportData(ExportMode.Excel);
    }, [handleExportData]);

    return (
        <>
            <ManageAllocationDimensionDescription
                isRollupMode={isRollupMode}
                onExportDataAsExcel={handleExportDataAsExcel}
            />
            <AllocationDimensionGrid
                allocationDimension={allocationDimension}
                onRowUpdated={handleOnRowUpdated}
                onRowMove={handleOnRowMove}
                hiddenColumns={[
                    AllocationDimensionValueFields.Active,
                    AllocationDimensionValueFields.RetailPriceField,
                    AllocationDimensionValueFields.CostPriceField,
                ]}
                uneditableColumns={[AllocationDimensionValueFields.ExternalId, AllocationDimensionValueFields.Name]}
                readOnly={readOnly}
                onCellEditing={setDisableEscapeKeyDown}
                purchaseOrderDoorGroups={merchantPurchaseOrderDoorGroups}
                unitsEntryType={unitsEntryType}
                onPurchaseOrdersError={onPurchaseOrdersError}
                onActionInvoked={handleActionInvoked}
                useLargeGrid={true}
                onCustomDoorCellValueChanged={onCustomDoorCellValueChanged}
                collectionsRefs={collectionsRefs}
                onGridReady={handleGridReady}
                onExportData={handleExportData}
            />
        </>
    );
};
